import type { TimeWindow } from '@provision/sidekick-utils';
import { Operator, RuleGroupArray, RuleGroupType, RuleType } from 'react-querybuilder';
import { FormField } from 'src/components/Forms/DynamicForm/dynamic-form';
import { UseQueryOptions } from 'src/pages/Settings/rules/use-rules-engine';
import { BehaviorRaw as BehaviorEntity } from 'src/types';
import {
  ActionType,
  BehaviorTrigger,
  ConditionType,
  DataConditionOperator,
  FormControlType,
} from './behavior-config';

export interface SelectorValue {
  value: string;
  label: string;
  name: string;
  uiType: FormControlType;
  conditionType?: ConditionType;
  controlType: RuleControlType;
  triggers: BehaviorTrigger[];
  entity?: string;
  fieldType?: string;
  operators?: Operator[];
  options: BehaviorConfigOption[];
  // 1V-2121:
  // We can't use normal `options` property name here, because react-querybuilder
  // has internal hardcoded logic which deletes options from condition fields if they're
  // duplicated in several fields. We have such situation with "hours-related" condition,
  // and it causes bugs.
  fieldOptions: BehaviorConfigOption[];
  optionsQuery?: keyof UseQueryOptions;
}

export enum RulesFormFields {
  Name = 'name',
  Description = 'description',
  BehaviorTrigger = 'behaviorTrigger',
  Conditions = 'conditions',
  Actions = 'actions',
}

export type CustomRuleType = RuleType<
  // @ts-expect-error Incorrect library typings do not support other value types except string.
  string | SelectorValue,
  DataConditionOperator,
  DataConditionValueType
>;
export type CustomRuleGroupType = RuleGroupType<
  // @ts-expect-error Incorrect library typings do not support other value types except string.
  CustomRuleType,
  'and' | 'or'
>;
export type RulesArray = RuleGroupArray<
  // @ts-expect-error Incorrect library typings do not support other value types except string.
  CustomRuleGroupType,
  CustomRuleType
>;

export type RulesFormActionRule =
  | CustomRuleGroupType
  | SendSlackMessageActionRule
  | SendConversationMessageActionRule
  | CreateConversationMessageDraftActionRule
  | WebhookActionRule
  | SignalActionRule
  | ManageConversationLabelsActionRule
  | ManageSubAssigneesActionRule
  | CreateTwilioTaskActionRule
  | RunTriageActionRule
  | DeleteConversationActionRule
  | BlockConversationRequesterActionRule;

export type RulesFormActions = {
  id: string;
  actions: RulesFormActionRule;
  actionType: ActionType;
}[];

export interface RulesFormModel {
  [RulesFormFields.Name]: string;
  [RulesFormFields.Description]: string;
  [RulesFormFields.BehaviorTrigger]: BehaviorTrigger;
  [RulesFormFields.Conditions]: RuleGroupType & { rules: RulesArray };
  [RulesFormFields.Actions]: RulesFormActions;
}

export enum RuleControlType {
  ConditionControl = 'condition-control',
  ActionControl = 'action-control',
}

export type UIActionField = SelectorValue | FormField;
export interface UIBehavior
  extends Omit<BehaviorEntity, 'conditions' | 'actions' | 'partner' | 'sequences'> {
  conditions: ConditionGroup;
  actions: SystemAction[];
  isDefault: boolean;
}

type ConditionElementType = 'conditionNode' | 'conditionGroup';

interface ConditionElementBase {
  elementType: ConditionElementType;
}

export interface DataConditionNode extends ConditionElementBase {
  elementType: 'conditionNode';
  conditionType: 'data';
  attribute: string;
  operator: DataConditionOperator;
  value: DataConditionValueType;
  dataType: DataConditionDataType;
  isRollingDate?: boolean;
}

interface ActorConditionNode extends ConditionElementBase {
  elementType: 'conditionNode';
  conditionType: 'actor';
  attribute: string;
  operator: DataConditionOperator;
  value: DataConditionValueType;
}

interface PartnerSettingConditionNode extends ConditionElementBase {
  elementType: 'conditionNode';
  conditionType: 'partnerSetting';
  attribute: string;
  operator: DataConditionOperator;
  value: DataConditionValueType;
}

export type DataConditionValueType =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | boolean[]
  | BehaviorConfigOption;

export enum DataConditionDataType {
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
  DateTime = 'dateTime',
}

export enum BusinessHoursType {
  Partner = 'partnerBusinessHours',
  ContactCenter = 'contactCenterHours',
  CustomSchedule = 'customSchedule',
}

export enum BusinessHoursConditionValue {
  In = 'in',
  NotIn = 'not_in',
}

export type BusinessHoursConditionNode =
  | OpenClosedHoursConditionNode
  | CustomScheduleConditionNode;

export interface OpenClosedHoursConditionNode {
  elementType: 'conditionNode';
  conditionType: 'businessHours';
  businessHoursType: BusinessHoursType.Partner | BusinessHoursType.ContactCenter;
  value: BusinessHoursConditionValue;
  /*
    Actual BE model for BusinessHoursConditionNode doesn't have an operator, but UI
    displays this field with a single available operator: `Equals`.
  */
  operator: DataConditionOperator;
}

/** One time range on one or more week days; the BE reads `operator` (in / not in the schedule). */
export interface CustomScheduleConditionNode {
  elementType: 'conditionNode';
  conditionType: 'businessHours';
  businessHoursType: BusinessHoursType.CustomSchedule;
  value: TimeWindow[];
  operator: DataConditionOperator.In | DataConditionOperator.NotIn;
}

export type ConditionNode =
  | DataConditionNode
  | BusinessHoursConditionNode
  | ActorConditionNode
  | PartnerSettingConditionNode;

export interface ConditionGroup extends ConditionElementBase {
  elementType: 'conditionGroup';
  conditionsElements: (ConditionNode | ConditionGroup)[];
  operator: 'and' | 'or';
}

export type ConditionElement = ConditionNode | ConditionGroup;

export interface ActionBase {
  id?: string;
  type: ActionType;
}

export interface DataActionRule extends ActionBase {
  type: ActionType.Data;
  attribute: string;
  value: string;
}

export enum SlackWorkspaceType {
  Partner = 'partner',
  Internal = 'internal',
}

export interface SendSlackMessageActionRule extends ActionBase {
  type: ActionType.SendSlackMessage;
  channel: string;
  workspaceType: SlackWorkspaceType | null;
  message: string;
}

/**
 * Where the sent message comes from. Derived from the stored action rather than
 * persisted on its own — the same call macros made for its text snippet — so the
 * source can never disagree with the data and no rule needs a backfill.
 */
export enum SendConversationMessageSource {
  StaticText = 'static',
  AiPrompt = 'ai',
}

export interface SendConversationMessageActionRule extends ActionBase {
  type: ActionType.SendConversationMessage;
  isPrivate: boolean | null;
  message: string;
  /** undefined / null / absent is treated as 'chat' (backward-compatible). */
  target?: 'chat' | 'sms_session' | null;
  /**
   * Inline custom prompt the AI writes the message from. Only the chosen branch
   * is persisted, so this and `message` are never both filled.
   */
  aiPrompt?: string | null;
  /**
   * Form-only: drives the source toggle and the `dependsOn` visibility of the
   * two bodies. Derived on load and dropped on save, never stored on the action.
   */
  messageSource?: SendConversationMessageSource | null;
}

export interface CreateConversationMessageDraftActionRule extends ActionBase {
  type: ActionType.CreateConversationMessageDraft;
  isPrivate: boolean | null;
  message: string;
  aiPrompt?: string | null;
  /** Form-only, same role as on the send action. */
  messageSource?: SendConversationMessageSource | null;
}

export interface WebhookActionRule extends ActionBase {
  type: ActionType.Webhook;
  url: string;
  method: string | null;
  body: string;
  headers: string;
}

export interface SignalActionRule extends ActionBase {
  type: ActionType.Signal;
  operation: string | null;
  notificationsTargetType: string | null;
}

export interface ManageConversationLabelsActionRule extends ActionBase {
  type: ActionType.ManageConversationLabels;
  labelIds: string[] | null;
  operation: 'add' | 'remove' | null;
}

export interface ManageSubAssigneesActionRule extends ActionBase {
  type: ActionType.ManageSubAssignees;
  platformUserIds: string[] | null;
  operation: 'add' | 'remove' | null;
}

export interface CreateTwilioTaskActionRule extends ActionBase {
  type: ActionType.CreateTwilioTask;
  taskChannel: string;
}

export interface RunTriageActionRule extends ActionBase {
  type: ActionType.RunTriage;
  userInstructions: string;
  conversationTriageId?: string | null;
}

export interface DeleteConversationActionRule extends ActionBase {
  type: ActionType.DeleteConversation;
}

export interface BlockConversationRequesterActionRule extends ActionBase {
  type: ActionType.BlockConversationRequester;
}

export type SystemAction =
  | DataActionRule
  | SendSlackMessageActionRule
  | SendConversationMessageActionRule
  | CreateConversationMessageDraftActionRule
  | WebhookActionRule
  | SignalActionRule
  | ManageConversationLabelsActionRule
  | ManageSubAssigneesActionRule
  | CreateTwilioTaskActionRule
  | RunTriageActionRule
  | DeleteConversationActionRule
  | BlockConversationRequesterActionRule;

export interface BehaviorConfigOption {
  id?: string | number | boolean;
  value: string | number | boolean;
  label: string;
  icon?: string;
  color?: string;
}
export interface BehaviorConfigOptionsQuery {
  graphqlQuery: string;
}

export function isDataAction(action: SystemAction): action is DataActionRule {
  return action.type === ActionType.Data;
}
